import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link2, Copy, Check, Plus, Ban, AlertCircle, Loader2 } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

// Links únicos para que personas sin usuario de Centro Sura respondan una
// encuesta. Mercadeo los genera aquí y los reparte por su cuenta (WhatsApp,
// correo, etc.). Cada link sirve para una sola respuesta; quien lo abre
// escribe nombre y apellido. Ver backend/routes/encuestas_publicas_routes.py.

const ESTADOS = {
    pendiente: { label: 'Sin responder', cls: 'bg-amber-50 text-amber-700' },
    respondida: { label: 'Respondido', cls: 'bg-teal-50 text-teal-700' },
    anulada: { label: 'Anulado', cls: 'bg-gray-100 text-gray-500' },
};

// navigator.clipboard solo existe en contextos seguros (https / localhost);
// en la red interna por IP cae al método viejo.
const copiarTexto = async (texto) => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(texto);
            return true;
        }
    } catch { /* cae al respaldo */ }
    const ta = document.createElement('textarea');
    ta.value = texto;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    document.body.removeChild(ta);
    return ok;
};

const InvitacionesExternasPanel = ({ encuesta, onCambio }) => {
    const [items, setItems] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [cantidad, setCantidad] = useState(5);
    const [etiqueta, setEtiqueta] = useState('');
    const [generando, setGenerando] = useState(false);
    const [error, setError] = useState('');
    const [copiado, setCopiado] = useState(null); // id | 'todos'
    const [confirmarAnular, setConfirmarAnular] = useState(null);

    const cargar = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get(`/api/encuestas-producto/${encuesta.id}/invitaciones`);
            setItems(data);
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudieron cargar los links');
        } finally {
            setCargando(false);
        }
    }, [encuesta.id]);

    useEffect(() => { cargar(); }, [cargar]);

    const vencida = encuesta.fechaLimite && new Date(encuesta.fechaLimite) < new Date(new Date().toDateString());
    const cerrada = !encuesta.activa || vencida;

    const conteo = useMemo(() => items.reduce((acc, x) => {
        acc[x.estado] = (acc[x.estado] || 0) + 1;
        return acc;
    }, {}), [items]);
    const pendientes = items.filter((x) => x.estado === 'pendiente');

    const generar = async (e) => {
        e.preventDefault();
        setError('');
        setGenerando(true);
        try {
            const { data } = await axiosInstance.post(`/api/encuestas-producto/${encuesta.id}/invitaciones`, {
                cantidad: Number(cantidad) || 1,
                etiqueta: etiqueta.trim() || null,
            });
            setItems((prev) => [...prev, ...data]);
            setEtiqueta('');
            onCambio?.();
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudieron generar los links');
        } finally {
            setGenerando(false);
        }
    };

    const guardarEtiqueta = async (inv, valor) => {
        const limpio = valor.trim();
        if ((inv.etiqueta || '') === limpio) return;
        try {
            const { data } = await axiosInstance.patch(`/api/encuestas-producto/${encuesta.id}/invitaciones/${inv.id}`, { etiqueta: limpio });
            setItems((prev) => prev.map((x) => (x.id === inv.id ? data : x)));
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo guardar la nota');
        }
    };

    const anular = async (inv) => {
        setConfirmarAnular(null);
        try {
            const { data } = await axiosInstance.delete(`/api/encuestas-producto/${encuesta.id}/invitaciones/${inv.id}`);
            setItems((prev) => prev.map((x) => (x.id === inv.id ? data : x)));
            onCambio?.();
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo anular el link');
        }
    };

    const copiar = async (clave, texto) => {
        if (await copiarTexto(texto)) {
            setCopiado(clave);
            setTimeout(() => setCopiado((c) => (c === clave ? null : c)), 1800);
        } else {
            setError('No se pudo copiar. Selecciona el link y cópialo a mano.');
        }
    };

    const copiarPendientes = () => copiar(
        'todos',
        pendientes.map((x) => (x.etiqueta ? `${x.etiqueta}: ${x.url}` : x.url)).join('\n')
    );

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center flex-shrink-0">
                        <Link2 size={18} />
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900">Links para personas sin usuario de Centro Sura</h3>
                        <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
                            Cada link sirve para una sola respuesta. Compártelo por WhatsApp o correo: quien lo abra
                            escribe su nombre y apellido y responde desde el teléfono, sin iniciar sesión.
                        </p>
                    </div>
                </div>

                {cerrada && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                        <AlertCircle size={15} className="flex-shrink-0" />
                        La encuesta está {vencida ? 'vencida' : 'desactivada'}: los links no aceptan respuestas hasta que la reabras.
                    </div>
                )}

                <form onSubmit={generar} className="mt-4 flex flex-wrap items-end gap-3">
                    <label className="block">
                        <span className="text-xs font-medium text-gray-500">Cantidad</span>
                        <input
                            type="number"
                            min={1}
                            max={50}
                            value={cantidad}
                            onChange={(e) => setCantidad(e.target.value)}
                            className="mt-1 w-24 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-teal-500 tabular-nums"
                        />
                    </label>
                    <label className="block flex-1 min-w-[220px]">
                        <span className="text-xs font-medium text-gray-500">Para quién (opcional)</span>
                        <input
                            value={etiqueta}
                            onChange={(e) => setEtiqueta(e.target.value)}
                            maxLength={100}
                            placeholder="Ej. Clientas Tienda Centro"
                            className="mt-1 w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-teal-500"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={generando}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-xl hover:bg-teal-700 disabled:opacity-50 transition-colors"
                    >
                        {generando ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                        Generar {Number(cantidad) === 1 ? 'link' : 'links'}
                    </button>
                </form>
                {error && (
                    <p className="mt-3 flex items-center gap-2 text-sm text-red-600"><AlertCircle size={14} />{error}</p>
                )}
            </div>

            {cargando ? (
                <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin text-teal-500" /></div>
            ) : items.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                    <p className="text-sm text-gray-500">Todavía no hay links. Genera los que necesites arriba.</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-gray-100">
                        <p className="text-sm text-gray-600 tabular-nums">
                            {conteo.respondida || 0} respondidos · {conteo.pendiente || 0} sin responder
                            {conteo.anulada ? ` · ${conteo.anulada} anulados` : ''}
                        </p>
                        {pendientes.length > 0 && (
                            <button
                                type="button"
                                onClick={copiarPendientes}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
                            >
                                {copiado === 'todos' ? <Check size={14} className="text-teal-600" /> : <Copy size={14} />}
                                {copiado === 'todos' ? 'Copiados' : `Copiar los ${pendientes.length} sin responder`}
                            </button>
                        )}
                    </div>
                    <ul className="divide-y divide-gray-100">
                        {items.map((inv) => {
                            const est = ESTADOS[inv.estado] || ESTADOS.pendiente;
                            return (
                                <li key={inv.id} className={`px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 ${inv.estado === 'anulada' ? 'opacity-60' : ''}`}>
                                    <input
                                        defaultValue={inv.etiqueta || ''}
                                        onBlur={(e) => guardarEtiqueta(inv, e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                        placeholder="Sin nota"
                                        maxLength={120}
                                        disabled={inv.estado === 'anulada'}
                                        className="w-44 px-2 py-1 text-sm text-gray-800 bg-transparent border border-transparent rounded-lg hover:border-gray-200 focus:border-teal-500 focus:outline-none"
                                        aria-label="Nota del link"
                                    />
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${est.cls}`}>{est.label}</span>
                                    <div className="flex-1 min-w-[200px]">
                                        {inv.estado === 'respondida' ? (
                                            <p className="text-sm text-gray-700">
                                                {inv.respondidoPor}
                                                {inv.usadaEn && (
                                                    <span className="text-gray-400"> · {new Date(inv.usadaEn).toLocaleDateString('es-VE', { day: 'numeric', month: 'short' })}</span>
                                                )}
                                            </p>
                                        ) : (
                                            <p className="text-xs text-gray-400 font-mono truncate" title={inv.url}>{inv.url}</p>
                                        )}
                                    </div>
                                    {inv.estado === 'pendiente' && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => copiar(inv.id, inv.url)}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
                                            >
                                                {copiado === inv.id ? <Check size={13} className="text-teal-600" /> : <Copy size={13} />}
                                                {copiado === inv.id ? 'Copiado' : 'Copiar link'}
                                            </button>
                                            {confirmarAnular === inv.id ? (
                                                <>
                                                    <button type="button" onClick={() => anular(inv)} className="px-2.5 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                                                        Anular
                                                    </button>
                                                    <button type="button" onClick={() => setConfirmarAnular(null)} className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700">
                                                        No
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setConfirmarAnular(inv.id)}
                                                    title="Anular: el link deja de funcionar"
                                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                                >
                                                    <Ban size={14} />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default InvitacionesExternasPanel;
